/**
 * Team vault client state (zustand).
 *
 * A SEPARATE store from `vaultStore`, on purpose and permanently. See the
 * header of `services/teamVaultService.js` for why merging team items into
 * `vault_list` is a security vulnerability rather than a tidy-up: two unscoped
 * `.find(it => it.name === '__e2e_keypair__')` lookups in `vaultShareCrypto.js`
 * would let anyone with team write access substitute another member's chat
 * identity key or permanently wedge their E2E.
 *
 * Consequences of that decision, all deliberate:
 *   - nothing in this file calls `invoke`; the transport is HTTP, not Tauri;
 *   - `vaultStore.items` is byte-identical whether or not the user is in teams;
 *   - consumers OPT IN by reading this store as well as that one.
 *
 * Holds ENVELOPES only — `GET /items` never returns `data`. The secret body is
 * fetched at the moment of use (`teamVaultService.getItem`), which is the
 * rate-limited and audited endpoint, and is never written into this store.
 *
 * SELECTOR CONVENTION (this cost a shipped bug on 05.08.2026): every exported
 * selector below builds a fresh array/object, so passing one straight to
 * `useTeamVaultStore(...)` makes `useSyncExternalStore` see a new snapshot on
 * every render and React aborts the panel with "Maximum update depth
 * exceeded". Subscribe to the raw slice and wrap the selector in `useMemo`, as
 * `VaultSidebar.js` does. `src/tests/teamVault.test.js` scans for violations.
 */
import { create } from 'zustand';
import teamVaultService, { teamVaultErrorMessage } from '../services/teamVaultService';
import { addSessionEndedListener, addTeamVaultDeltaListener } from '../services/channelService';

const initialState = {
  // String(teamId) -> {
  //   folders: [Folder], root: { can_read, can_write }, canAdminister: bool,
  //   items: [Item], loading: bool, error: string|null, loaded: bool
  // }
  byTeam: {},
};

/** The empty entry every reader falls back to. Frozen — never mutated. */
export const EMPTY_TEAM_ENTRY = Object.freeze({
  folders: Object.freeze([]),
  root: Object.freeze({ can_read: false, can_write: false }),
  canAdminister: false,
  // Full write scope: may create/rename/delete this team's folders. Not the
  // same question as `root.can_write` — see teamVaultService.listFolders.
  canManageFolders: false,
  items: Object.freeze([]),
  loading: false,
  error: null,
  loaded: false,
});

const key = (teamId) => String(teamId);

export const useTeamVaultStore = create((set, get) => ({
  ...initialState,

  /**
   * Pull this team's authorized view: folders + item envelopes.
   *
   * Both come back scoped to what the caller may read — the server decides,
   * not this store. A team the user cannot read at all answers 404 (never
   * confirming the vault exists), which lands in `error` and leaves `items`
   * empty rather than throwing into a render path.
   */
  loadTeam: async (teamId) => {
    if (teamId == null) return;
    const k = key(teamId);
    set((s) => ({
      byTeam: {
        ...s.byTeam,
        [k]: { ...(s.byTeam[k] || EMPTY_TEAM_ENTRY), loading: true, error: null },
      },
    }));
    try {
      const [folderInfo, items] = await Promise.all([
        teamVaultService.listFolders(teamId),
        teamVaultService.listItems(teamId),
      ]);
      set((s) => ({
        byTeam: {
          ...s.byTeam,
          [k]: {
            folders: folderInfo.folders,
            root: folderInfo.root,
            canAdminister: folderInfo.canAdminister,
            canManageFolders: folderInfo.canManageFolders,
            items,
            loading: false,
            error: null,
            loaded: true,
          },
        },
      }));
    } catch (e) {
      set((s) => ({
        byTeam: {
          ...s.byTeam,
          [k]: {
            ...(s.byTeam[k] || EMPTY_TEAM_ENTRY),
            loading: false,
            error: teamVaultErrorMessage(e),
            loaded: true,
          },
        },
      }));
    }
  },

  /**
   * Refresh a team this store already knows about.
   *
   * The realtime nudge routes here. It refreshes ONLY teams already in
   * `byTeam`: the payload's `team_id` arrives over the wire, and a client that
   * fetched whatever id it was handed would turn a nudge into a request-forcing
   * primitive. The server would refuse an unauthorized team anyway — this makes
   * the client not ask in the first place.
   */
  refreshTeam: async (teamId) => {
    if (teamId == null) return;
    if (!get().byTeam[key(teamId)]) return;
    await get().loadTeam(teamId);
  },

  /** Drop one team's cache (left the team, or lost every grant). */
  forgetTeam: (teamId) =>
    set((s) => {
      const k = key(teamId);
      if (!s.byTeam[k]) return s;
      const byTeam = { ...s.byTeam };
      delete byTeam[k];
      return { byTeam };
    }),

  reset: () => set({ ...initialState }),
}));

// Sign-out empties this store like every other account-scoped cache. Without
// it the next account's first render sees the departed one's team inventory —
// hosts, usernames and folder names — until something happens to refetch.
addSessionEndedListener(() => {
  useTeamVaultStore.getState().reset();
});

/**
 * Realtime (item 5): `team_vault_delta` carries EXACTLY `{ team_id }` — no
 * name, no folder, no secret, no operation. Pull the authorized view on
 * receipt; the payload is a cursor, never data.
 *
 * Registered once at module load, mirroring `vaultStore`'s session-ended
 * subscription, so no component has to be mounted for a delta to land.
 */
export function handleTeamVaultDelta(payload) {
  const teamId = payload?.team_id;
  // Only `team_id` is read. Anything else the payload happens to carry is
  // ignored on purpose: the client re-derives its authorized view from the
  // server rather than believing a broadcast about what changed.
  if (teamId == null) return Promise.resolve();
  // The promise is returned rather than dropped so callers CAN await it. The
  // channel handler does not, and must not — a slow pull cannot be allowed to
  // block event delivery — but a test that cannot await this has to guess at
  // microtask depth, which is how a realtime test quietly becomes vacuous.
  return useTeamVaultStore.getState().refreshTeam(teamId);
}

addTeamVaultDeltaListener(handleTeamVaultDelta);

// ── selectors (pure; NEVER pass one straight to the hook — see header) ───────

/** This team's entry, or the frozen empty one. */
export function selectTeamEntry(byTeam, teamId) {
  return (byTeam && byTeam[key(teamId)]) || EMPTY_TEAM_ENTRY;
}

/** Folders in this team the user may WRITE to. */
export function selectWritableFolders(byTeam, teamId) {
  return selectTeamEntry(byTeam, teamId).folders.filter((f) => f && f.can_write);
}

/**
 * Can the user write ANYWHERE in this team's vault — root or any folder?
 *
 * The destination picker keys off this: offering a team the server will reject
 * means the user fills in an entire credential form and is refused at save.
 */
export function selectCanWriteSomewhere(byTeam, teamId) {
  const entry = selectTeamEntry(byTeam, teamId);
  return !!entry.root?.can_write || entry.folders.some((f) => f && f.can_write);
}

export const resetTeamVaultStore = () => useTeamVaultStore.getState().reset();
export const loadTeamVault = (teamId) => useTeamVaultStore.getState().loadTeam(teamId);
export const refreshTeamVault = (teamId) => useTeamVaultStore.getState().refreshTeam(teamId);
